import { useEffect, useState } from "react";
import { AccessibilityInfo, Platform } from "react-native";

export type ReducedMotionPreference = {
  /** Conservative until the platform preference has been resolved. */
  reduceMotion: boolean;
  ready: boolean;
};

const conservativeDefault: ReducedMotionPreference = {
  reduceMotion: true,
  ready: false,
};

/**
 * Read the platform preference once and subscribe to changes.
 *
 * The conservative initial value is intentional: a user who has requested
 * reduced motion must never receive a first-render animation while the native
 * or browser preference is still resolving.
 */
export function useReducedMotion(): ReducedMotionPreference {
  const [preference, setPreference] = useState<ReducedMotionPreference>(
    conservativeDefault,
  );

  useEffect(() => {
    let mounted = true;

    if (Platform.OS === "web" && typeof window !== "undefined" && window.matchMedia) {
      const query = window.matchMedia("(prefers-reduced-motion: reduce)");
      const update = (event?: MediaQueryListEvent) => {
        if (mounted) {
          setPreference({
            reduceMotion: event?.matches ?? query.matches,
            ready: true,
          });
        }
      };

      update();
      if (query.addEventListener) {
        query.addEventListener("change", update);
      } else {
        query.addListener(update);
      }

      return () => {
        mounted = false;
        if (query.removeEventListener) {
          query.removeEventListener("change", update);
        } else {
          query.removeListener(update);
        }
      };
    }

    AccessibilityInfo.isReduceMotionEnabled()
      .then((enabled) => {
        if (mounted) {
          setPreference({ reduceMotion: enabled, ready: true });
        }
      })
      .catch(() => {
        // Keep the conservative value if the platform cannot report a setting.
        if (mounted) setPreference({ reduceMotion: true, ready: true });
      });

    const subscription = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      (enabled) => {
        if (mounted) setPreference({ reduceMotion: enabled, ready: true });
      },
    );

    return () => {
      mounted = false;
      subscription.remove();
    };
  }, []);

  return preference;
}
